import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { LoadStatus } from '../../load-status';
import { excludeUnmirroredKeys } from '../user-store.converters';
import {
    USER_STORE_FEATURE_KEY,
    UserStoreEntriesState,
    UserStorePersistedState,
    UserStoreState,
} from '../user-store.state';

export const selectUserStoreFeature = createFeatureSelector<UserStoreState>(USER_STORE_FEATURE_KEY);

/** The mirrored entries, keyed by store key. */
export const selectUserStoreEntries: MemoizedSelector<object, UserStoreEntriesState> = createSelector(
    selectUserStoreFeature,
    (state) => state.entries,
);

/** Raw load status of the slice. */
export const selectUserStoreStatus: MemoizedSelector<object, LoadStatus> = createSelector(
    selectUserStoreFeature,
    (state) => state.status,
);

/** Whether the mirror has been populated, from the backend or its persisted cache. */
export const selectUserStoreLoaded: MemoizedSelector<object, boolean> = createSelector(
    selectUserStoreStatus,
    (status) => status === LoadStatus.Loaded,
);

/**
 * Whether the bulk backend GET has finished, successfully or not — as opposed to
 * {@link selectUserStoreLoaded}, which a cache rehydration also satisfies. Use this to tell a
 * confirmed-current mirror from one that is only cached.
 */
export const selectUserStoreSettled: MemoizedSelector<object, boolean> = createSelector(
    selectUserStoreFeature,
    (state) => state.hasBackendSettled,
);

/** Keys whose DELETE has not been confirmed by the backend yet — see `UserStoreState.pendingDeletes`. */
export const selectUserStorePendingDeletes: MemoizedSelector<object, readonly string[]> = createSelector(
    selectUserStoreFeature,
    (state) => state.pendingDeletes,
);

/** Factory selector: the mirrored value for one key, or `undefined` if absent, unmirrored, or not yet loaded. */
export function selectUserStoreValue(key: string): MemoizedSelector<object, unknown> {
    return createSelector(selectUserStoreEntries, (entries) => entries[key]);
}

/** Factory selector: whether `key` currently has a mirrored value. */
export function selectHasUserStoreValue(key: string): MemoizedSelector<object, boolean> {
    return createSelector(selectUserStoreEntries, (entries) => key in entries);
}

/**
 * Builds the slice's persisted projection for a given exclusion list — now covering both the
 * mirrored entries AND `pendingDeletes` (see {@link UserStorePersistedState}), so an offline
 * delete survives a restart just like an offline upsert. `UserStoreStoreModule` builds its
 * `PERSISTED_SLICES` registration from this via a factory provider that injects
 * `USER_STORE_UNMIRRORED_KEYS`, so the exclusion list only has to be provided once, at the
 * injection token. This re-applies the exclusion as a second line of defence alongside the
 * effects' own filtering (see `USER_STORE_UNMIRRORED_KEYS`'s doc comment): a bug that let an
 * excluded key slip into the in-memory slice still cannot leak it into IndexedDB, in either half
 * of the projection.
 */
export function createPersistedUserStoreSelector(
    unmirroredKeys: readonly string[],
): MemoizedSelector<object, UserStorePersistedState> {
    return createSelector(selectUserStoreEntries, selectUserStorePendingDeletes, (entries, pendingDeletes) => ({
        entries: excludeUnmirroredKeys(entries, unmirroredKeys),
        pendingDeletes: pendingDeletes.filter((key) => !unmirroredKeys.includes(key)),
    }));
}
